
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { IPublicClientApplication } from '@azure/msal-browser';
import { SUCCESS_MESSAGES, ERROR_MESSAGES, NOTIFICATION_SEVERITY } from '../../constants';
import { setStatusBar } from '../modals/modalSlice';
import { executeProposal } from '../chat/chatSlice';
import { mergeAdjacentText, untagEntreeEverywhere } from '../../components/mobile/notes/entreeRange';
import type { PorteeRapprochement } from '../../types/api';
import { lirePorteeMemorisee } from './porteeMemorisee';

// Interface pour les données d'entrée sauvegardées
interface SavedEntreeData {
    entree: any;
    // Note: instance n'est pas stockée dans Redux pour éviter la sérialisation
}

// Thunk async pour récupérer toutes les entrées
export const fetchEntreesAsync = createAsyncThunk(
    'entrees/fetchEntreesAsync',
    async (instance: IPublicClientApplication, { rejectWithValue }) => {
        try {
            // Obtenir le token d'authentification
            const { getTokenRedirect } = await import('../../utils/authRedirect');
            const { tokenRequest } = await import('../../authConfig');
            
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) {
                throw new Error('Impossible d\'obtenir le token d\'authentification');
            }

            // Appel API pour récupérer toutes les entrées (avec une limite élevée pour tout récupérer)
            const response = await fetch('/api/entrees?limit=1000', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accesstoken': token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Erreur API: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const responseData = await response.json();
            console.log('✅ Toutes les entrées récupérées:', responseData.data?.length || 0);
            
            return responseData.data || responseData;
        } catch (error: any) {
            console.error('❌ Erreur lors de la récupération des entrées:', error.message);
            return rejectWithValue(error.message || 'Erreur lors de la récupération des entrées');
        }
    }
);

// Thunk async pour rechercher des entrées
export const searchEntriesAsync = createAsyncThunk(
    'entrees/searchEntriesAsync',
    async ({ searchTerm, instance }: { searchTerm: string; instance: IPublicClientApplication }, { rejectWithValue, dispatch }) => {
        try {
            // Obtenir le token d'authentification
            const { getTokenRedirect } = await import('../../utils/authRedirect');
            const { tokenRequest } = await import('../../authConfig');
            
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) {
                throw new Error('Impossible d\'obtenir le token d\'authentification');
            }

            // Appel API pour rechercher les entrées
            const response = await fetch(`/api/entrees?search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accesstoken': token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Erreur API: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const responseData = await response.json();
            console.log('✅ Résultats de recherche:', responseData);
            
            return responseData.data || responseData;
        } catch (error: any) {
            console.error('❌ Erreur lors de la recherche:', error.message);
            return rejectWithValue(error.message || 'Erreur lors de la recherche');
        }
    }
);

// Thunk async pour sauvegarder une entrée (API + Redux hybride)
export const saveAsync = createAsyncThunk(
    'entrees/saveAsync',
    // `silencieux` : pas de notification de SUCCÈS (l'appelant dit déjà ce qui s'est passé,
    // ex. la note historique du geste « Faire émerger un concept ») ; l'erreur est toujours dite.
    async ({ entree, instance, silencieux = false }: { entree: any; instance: IPublicClientApplication; silencieux?: boolean }, { rejectWithValue, dispatch }) => {
        try {
            // Obtenir le token d'authentification
            const { getTokenRedirect } = await import('../../utils/authRedirect');
            const { tokenRequest } = await import('../../authConfig');

            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) {
                throw new Error('Impossible d\'obtenir le token d\'authentification');
            }

            // Déterminer si c'est une création ou une mise à jour
            const isUpdate = Boolean(entree.id || entree._id);
            const entreeId = entree.id || entree._id;
            
            // Utiliser POST pour création, PUT pour mise à jour
            const url = isUpdate ? `/api/entrees/${entreeId}` : '/api/entrees';
            const method = isUpdate ? 'PUT' : 'POST';
            
            console.log(`📤 ${method} ${url}`, entree);

            // Appel API réel pour sauvegarder l'entrée
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accesstoken': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(entree)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Erreur API: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const responseData = await response.json();
            console.log('✅ Réponse API complète:', responseData);
            
            // Extraire les données de l'entrée depuis la réponse API
            const savedEntree = responseData.data || responseData;
            console.log('✅ Entrée sauvegardée avec succès:', savedEntree);

            // Notification de succès
            if (!silencieux) {
                const message = isUpdate ? SUCCESS_MESSAGES.ENTREE_UPDATED : SUCCESS_MESSAGES.ENTREE_CREATED;
                dispatch(setStatusBar({
                    open: true,
                    message,
                    severity: NOTIFICATION_SEVERITY.SUCCESS
                }));
            }
            
            // Retourner seulement les données sérialisables
            return savedEntree;
        } catch (error: any) {
            console.error('❌ Erreur lors de la sauvegarde:', error.message);
            
            // Notification d'erreur
            dispatch(setStatusBar({
                open: true,
                message: `Erreur lors de la sauvegarde: ${error.message}`,
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            
            return rejectWithValue(error.message || 'Erreur lors de la sauvegarde');
        }
    }
);

// Thunk async pour supprimer une entrée (API + Redux hybride)
export const removeAsync = createAsyncThunk(
    'entrees/removeAsync',
    async ({ entree, instance }: { entree: any; instance: IPublicClientApplication }, { rejectWithValue, dispatch }) => {
        try {
            // Obtenir le token d'authentification
            const { getTokenRedirect } = await import('../../utils/authRedirect');
            const { tokenRequest } = await import('../../authConfig');
            
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) {
                throw new Error('Impossible d\'obtenir le token d\'authentification');
            }

            // Récupérer l'ID de l'entrée (supporte _id et id)
            const entreeId = entree._id || entree.id;
            if (!entreeId) {
                throw new Error('ID de l\'entrée manquant pour la suppression');
            }

            // Appel API réel pour supprimer l'entrée - L'ID doit être dans l'URL
            const response = await fetch(`/api/entrees/${entreeId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accesstoken': token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Erreur API: ${response.status} ${response.statusText}`);
            }

            // Pour la suppression, l'API ne renvoie pas les données de l'entrée
            // On retourne l'entrée originale pour que les extraReducers puissent l'identifier
            console.log('✅ Entrée supprimée avec succès:', entreeId);

            // Notification de succès
            dispatch(setStatusBar({
                open: true,
                message: SUCCESS_MESSAGES.ENTREE_DELETED,
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            // Retourner l'entrée supprimée pour mise à jour du store
            return entree;
        } catch (error: any) {
            console.error('❌ Erreur lors de la suppression:', error.message);
            
            // Notification d'erreur
            dispatch(setStatusBar({
                open: true,
                message: `Erreur lors de la suppression: ${error.message}`,
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            
            return rejectWithValue(error.message || 'Erreur lors de la suppression');
        }
    }
);

/**
 * Parcourt récursivement un nœud Lexical et met à jour les entrées correspondantes
 * @param node - Le nœud à parcourir
 * @param entreeId - L'ID de l'entrée à modifier
 * @param oldCle - L'ancienne clé
 * @param newCle - La nouvelle clé
 * @returns true si des modifications ont été faites
 */
export function updateEntreeInLexicalNode(node: any, entreeId: string, oldCle: string, newCle: string): boolean {
    if (!node) return false;
    
    let modified = false;
    
    // Vérifier si c'est un nœud EntreeNode
    if (node.type === 'entree' || node.type === 'entrees') {
        // Format avec tableau 'entrees'
        if (node.entrees && Array.isArray(node.entrees)) {
            for (const entreeData of node.entrees) {
                if ((entreeData.id === entreeId || entreeData._id === entreeId) || entreeData.cle === oldCle) {
                    entreeData.cle = newCle;
                    if (entreeId) {
                        entreeData.id = entreeId;
                    }
                    modified = true;
                    console.log(`  📝 Mise à jour EntreeNode (entrees[]): "${oldCle}" -> "${newCle}"`);
                }
            }
        }
        
        // Format avec données directes sur le nœud
        if (node.cle === oldCle || node.id === entreeId || node._id === entreeId) {
            node.cle = newCle;
            if (entreeId) {
                node.id = entreeId;
            }
            modified = true;
            console.log(`  📝 Mise à jour EntreeNode (direct): "${oldCle}" -> "${newCle}"`);
        }
        
        // Format avec entreeData
        if (node.entreeData && (node.entreeData.cle === oldCle || node.entreeData.id === entreeId || node.entreeData._id === entreeId)) {
            node.entreeData.cle = newCle;
            if (entreeId) {
                node.entreeData.id = entreeId;
            }
            modified = true;
            console.log(`  📝 Mise à jour EntreeNode (entreeData): "${oldCle}" -> "${newCle}"`);
        }
    }
    
    // Parcourir les enfants récursivement
    if (node.children && Array.isArray(node.children)) {
        for (const child of node.children) {
            if (updateEntreeInLexicalNode(child, entreeId, oldCle, newCle)) {
                modified = true;
            }
        }
    }
    
    return modified;
}

/** Champs de mise en forme d'un nœud texte Lexical, recopiés quand une marque redevient du texte. */
const TEXT_FIELDS = ['detail', 'format', 'mode', 'style', 'version'] as const;
const TEXT_DEFAULTS: Record<(typeof TEXT_FIELDS)[number], unknown> = { detail: 0, format: 0, mode: 'normal', style: '', version: 1 };

/**
 * Retire un concept supprimé du JSON Lexical d'une note (C22 SP2, revue finale).
 * Le texte de la note ne change JAMAIS : aucun nœud n'est retiré de l'arbre.
 * 1. Par identifiant (prioritaire) : `untagEntreeEverywhere` retire le concept de
 *    toutes ses marques ; un passage qui ne porte plus ni concept ni note reliée
 *    redevient du texte, un passage encore relié reste un nœud `entrees` sans concept.
 * 2. Résidus, selon les mêmes règles : marques de texte vide (que le parcours des
 *    marques ignore) et données anciennes sans identifiant, reconnues par leur clé.
 */
export function retirerConceptDuJSON<T>(json: T, entreeId: string | null, entreeCle: string | null): { json: T; changed: boolean } {
    let out = json;
    let changed = false;
    if (entreeId) {
        const result = untagEntreeEverywhere(json, entreeId);
        out = result.json;
        changed = result.changed;
    }

    const vise = (item: any): boolean => !!item && typeof item === 'object' && (
        (!!entreeId && (item.id === entreeId || item._id === entreeId))
        || (!!entreeCle && !item.id && !item._id && item.cle === entreeCle)
    );
    const porteEncore = (node: any): boolean =>
        (Array.isArray(node.entrees) && node.entrees.length > 0)
        || (Array.isArray(node.entreeList) && node.entreeList.length > 0)
        || (typeof node.cle === 'string' && node.cle !== '')
        || (!!node.entreeData && !!node.entreeData.cle)
        || (Array.isArray(node.noteRefs) && node.noteRefs.length > 0);

    const copy: any = JSON.parse(JSON.stringify(out));
    let residu = false;
    const visit = (node: any): void => {
        if (!node || typeof node !== 'object' || !Array.isArray(node.children)) return;
        node.children.forEach((child: any, index: number) => {
            if (!child || typeof child !== 'object') return;
            if (child.type === 'entree' || child.type === 'entrees') {
                let touche = false;
                for (const champ of ['entrees', 'entreeList'] as const) {
                    if (!Array.isArray(child[champ])) continue;
                    const gardes = child[champ].filter((item: any) => !vise(item));
                    if (gardes.length !== child[champ].length) { child[champ] = gardes; touche = true; }
                }
                if (vise(child)) { delete child.cle; delete child.id; delete child._id; touche = true; }
                if (child.entreeData && vise(child.entreeData)) { delete child.entreeData; touche = true; }
                if (touche) {
                    residu = true;
                    if (!porteEncore(child)) {
                        const texte: any = { type: 'text' };
                        for (const f of TEXT_FIELDS) texte[f] = child[f] !== undefined ? child[f] : TEXT_DEFAULTS[f];
                        texte.text = typeof child.text === 'string' ? child.text : '';
                        node.children[index] = texte;
                    }
                }
            }
            visit(child);
        });
    };
    visit(copy.root);
    if (residu) return { json: mergeAdjacentText(copy as T), changed: true };
    return { json: out, changed };
}

/**
 * Met à jour la clé d'une entrée dans toutes les notes qui la référencent
 * @param oldEntree - L'entrée avant modification (avec l'ancienne clé)
 * @param newEntree - L'entrée après modification (avec la nouvelle clé)
 * @param notes - Liste des notes à vérifier
 * @param callbackDispatch - Fonction pour sauvegarder les notes modifiées
 * @param instance - Instance MSAL pour l'authentification
 */
export const modifyEntreeInNote = async (
    oldEntree: any,
    newEntree: any,
    notes: any[],
    callbackDispatch: (obj: { note: any; instance: any }) => void,
    instance: any
) => {
    if (!notes || notes.length === 0) {
        console.log('⚠️ modifyEntreeInNote: Aucune note à traiter');
        return;
    }

    const entreeId = oldEntree._id || oldEntree.id;
    const oldCle = oldEntree.cle;
    const newCle = newEntree.cle;

    if (!entreeId) {
        console.warn('⚠️ modifyEntreeInNote: Pas d\'ID trouvé pour l\'entrée');
        return;
    }

    if (oldCle === newCle) {
        console.log('ℹ️ modifyEntreeInNote: La clé n\'a pas changé, pas de mise à jour nécessaire');
        return;
    }

    console.log(`🔄 modifyEntreeInNote: Mise à jour de la clé "${oldCle}" vers "${newCle}" (ID: ${entreeId})`);

    // Trouver toutes les notes qui contiennent cette entrée (dans cles OU dans content.JSON)
    const notesToUpdate = notes.filter((note: any) => {
        // Vérifier dans le tableau cles - PRIORITÉ À L'ID
        const inCles = note.cles && Array.isArray(note.cles) && note.cles.some((cleRef: any) => {
            // Priorité 1: correspondance par ID (identifiant unique)
            if (entreeId && cleRef.id === entreeId) return true;
            // Priorité 2: si la référence n'a pas d'ID, vérifier par clé (rétrocompatibilité)
            if (!cleRef.id && cleRef.cle === oldCle) return true;
            return false;
        });
        
        // Vérifier aussi dans content.JSON (recherche par ID prioritaire)
        const inContent = note.content?.JSON && (
            (entreeId && JSON.stringify(note.content.JSON).includes(entreeId)) ||
            JSON.stringify(note.content.JSON).includes(`"cle":"${oldCle}"`)
        );
        
        return inCles || inContent;
    });

    console.log(`📝 ${notesToUpdate.length} note(s) contiennent l'entrée à mettre à jour`);

    // Mettre à jour chaque note
    for (const note of notesToUpdate) {
        // 1. Mettre à jour le tableau cles - PRIORITÉ À L'ID
        const updatedCles = (note.cles || []).map((cleRef: any) => {
            // Priorité 1: correspondance par ID (identifiant unique)
            if (entreeId && cleRef.id === entreeId) {
                return {
                    ...cleRef,
                    cle: newCle,
                    id: entreeId
                };
            }
            // Priorité 2: si la référence n'a pas d'ID, vérifier par clé (rétrocompatibilité)
            if (!cleRef.id && cleRef.cle === oldCle) {
                return {
                    ...cleRef,
                    cle: newCle,
                    id: entreeId // Ajouter l'ID pour les futures recherches
                };
            }
            return cleRef;
        });

        // 2. Mettre à jour content.JSON (deep clone pour éviter les mutations)
        let updatedContentJSON = note.content?.JSON;
        let jsonModified = false;
        
        if (updatedContentJSON) {
            updatedContentJSON = JSON.parse(JSON.stringify(updatedContentJSON));
            if (updatedContentJSON.root) {
                jsonModified = updateEntreeInLexicalNode(updatedContentJSON.root, entreeId, oldCle, newCle);
            }
        }

        const updatedNote = {
            ...note,
            cles: updatedCles,
            content: {
                ...note.content,
                JSON: updatedContentJSON
            },
            updatedDate: new Date()
        };

        console.log(`✏️ Mise à jour de la note "${note.title || note._id}" - cles: ✓, JSON: ${jsonModified ? '✓' : '○'}`);
        
        try {
            await callbackDispatch({ note: updatedNote, instance });
        } catch (error) {
            console.error(`❌ Erreur lors de la mise à jour de la note ${note._id}:`, error);
        }
    }

    console.log('✅ modifyEntreeInNote: Toutes les notes ont été mises à jour');
};

/**
 * Supprime une entrée de toutes les notes qui la référencent
 * @param entree - L'entrée à supprimer
 * @param notes - Liste des notes à vérifier
 * @param callbackDispatch - Fonction pour sauvegarder les notes modifiées
 * @param instance - Instance MSAL pour l'authentification
 */
export const deleteEntreeInNote = async (
    entree: any,
    notes: any[],
    callbackDispatch: (obj: { note: any; instance: any }) => void,
    instance: any
) => {
    if (!notes || notes.length === 0) {
        console.log('⚠️ deleteEntreeInNote: Aucune note à traiter');
        return;
    }

    const entreeId = entree._id || entree.id;
    const entreeCle = entree.cle;

    if (!entreeId && !entreeCle) {
        console.warn('⚠️ deleteEntreeInNote: Pas d\'ID ni de clé trouvée pour l\'entrée');
        return;
    }

    console.log(`🗑️ deleteEntreeInNote: Suppression de l'entrée "${entreeCle}" (ID: ${entreeId}) des notes`);

    // Trouver toutes les notes qui contiennent cette entrée (dans cles OU dans content.JSON)
    const notesToUpdate = notes.filter((note: any) => {
        // Vérifier dans le tableau cles - PRIORITÉ À L'ID
        const inCles = note.cles && Array.isArray(note.cles) && note.cles.some((cleRef: any) => {
            // Priorité 1: correspondance par ID (identifiant unique)
            if (entreeId && cleRef.id === entreeId) return true;
            // Priorité 2: si la référence n'a pas d'ID, vérifier par clé (rétrocompatibilité)
            if (!cleRef.id && entreeCle && cleRef.cle === entreeCle) return true;
            return false;
        });
        
        // Vérifier aussi dans content.JSON (recherche par ID prioritaire)
        const inContent = note.content?.JSON && (
            (entreeId && JSON.stringify(note.content.JSON).includes(entreeId)) ||
            (entreeCle && JSON.stringify(note.content.JSON).includes(`"cle":"${entreeCle}"`))
        );
        
        return inCles || inContent;
    });

    console.log(`📝 ${notesToUpdate.length} note(s) contiennent l'entrée à supprimer`);

    // Mettre à jour chaque note en supprimant l'entrée
    for (const note of notesToUpdate) {
        // 1. Filtrer le tableau cles - PRIORITÉ À L'ID
        const updatedCles = (note.cles || []).filter((cleRef: any) => {
            // Priorité 1: correspondance par ID (identifiant unique)
            if (entreeId && cleRef.id === entreeId) return false;
            // Priorité 2: si la référence n'a pas d'ID, vérifier par clé (rétrocompatibilité)
            if (!cleRef.id && entreeCle && cleRef.cle === entreeCle) return false;
            return true;
        });

        // 2. Retirer le concept de content.JSON (copie) — sans jamais retirer de texte
        //    ni de note reliée (C22 SP2).
        let updatedContentJSON = note.content?.JSON;
        let jsonModified = false;

        if (updatedContentJSON?.root) {
            const result = retirerConceptDuJSON(updatedContentJSON, entreeId || null, entreeCle || null);
            updatedContentJSON = result.json;
            jsonModified = result.changed;
        }

        const updatedNote = {
            ...note,
            cles: updatedCles,
            content: {
                ...note.content,
                JSON: updatedContentJSON
            },
            updatedDate: new Date()
        };

        console.log(`✏️ Suppression de l'entrée de la note "${note.title || note._id}" - cles: ✓, JSON: ${jsonModified ? '✓' : '○'}`);
        
        try {
            await callbackDispatch({ note: updatedNote, instance });
        } catch (error) {
            console.error(`❌ Erreur lors de la mise à jour de la note ${note._id}:`, error);
        }
    }

    console.log('✅ deleteEntreeInNote: L\'entrée a été supprimée de toutes les notes');
};

interface EntreeState {
    data: any[];
    status: string;
    activeEntree?: any;
    activeRefsEntree?: any;
    error?: any;
    /** La portée de la liste des concepts, une seule avec « Rapprocher » (C31 bis SP1) ; mémorisée par navigateur. */
    portee: PorteeRapprochement;
}

const initialState: EntreeState = {
    data: [],
    status: 'idle',
    activeEntree: null,
    activeRefsEntree: null,
    error: null,
    portee: lirePorteeMemorisee(),
};

const entreeSlice = createSlice({
    name: 'entrees',
    initialState,
    reducers: {
        activateEntree(state, action: PayloadAction<any>) {
            state.activeEntree = action.payload;
        },
        setActiveRefsEntree(state, action: PayloadAction<any>) {
            state.activeRefsEntree = action.payload;
        },
        setEntrees(state, action: PayloadAction<any[]>) {
            state.data = action.payload;
            state.status = 'succeeded';
        },
        setLoading(state) {
            state.status = 'loading';
        },
        setError(state, action: PayloadAction<any>) {
            state.status = 'failed';
            state.error = action.payload;
        },
        setSucceeded(state) {
            state.status = 'succeeded';
        },
        /** La portée de la liste et de « Rapprocher » ; la mémoire par navigateur est l'affaire de l'appelant (`ecrirePorteeMemorisee`). */
        setPortee(state, action: PayloadAction<PorteeRapprochement>) {
            state.portee = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Gestion de fetchEntreesAsync
            .addCase(fetchEntreesAsync.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchEntreesAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload || [];
            })
            .addCase(fetchEntreesAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Gestion de saveAsync
            .addCase(saveAsync.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(saveAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Gérer les IDs MongoDB (_id) et les IDs frontend (id)
                const savedData = action.payload; // Déjà extrait dans le thunk
                const savedId = savedData._id || savedData.id;
                
                if (!savedId) {
                    console.warn('⚠️ Pas d\'ID trouvé dans les données sauvegardées:', savedData);
                    return;
                }
                
                // Ajouter ou mettre à jour l'entrée dans la liste
                const existingIndex = state.data.findIndex((e: any) => {
                    const currentId = e._id || e.id;
                    return currentId === savedId;
                });
                
                if (existingIndex >= 0) {
                    // Mise à jour d'une entrée existante - mise à jour fluide sans rechargement
                    state.data[existingIndex] = savedData;
                    console.log('🔄 Mise à jour fluide dans le store:', savedId);
                } else {
                    // Nouvelle entrée - ajout fluide
                    state.data.push(savedData);
                    console.log('➕ Ajout fluide au store:', savedId);
                }
            })
            .addCase(saveAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Gestion de removeAsync
            .addCase(removeAsync.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(removeAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Gérer les IDs MongoDB (_id) et les IDs frontend (id)
                const deletedData = action.payload; // L'entrée originale passée au thunk
                const deletedId = deletedData._id || deletedData.id;
                
                if (!deletedId) {
                    console.warn('⚠️ Pas d\'ID trouvé dans l\'entrée à supprimer:', deletedData);
                    return;
                }
                
                // Supprimer l'entrée de la liste - suppression fluide
                const initialLength = state.data.length;
                state.data = state.data.filter((e: any) => {
                    const currentId = e._id || e.id;
                    return currentId !== deletedId;
                });
                
                const finalLength = state.data.length;
                if (finalLength < initialLength) {
                    console.log(`🗑️ Suppression fluide du store: ${deletedId} (${initialLength} -> ${finalLength} entrées)`);
                } else {
                    console.warn('⚠️ Aucune entrée supprimée du store, ID non trouvé:', deletedId);
                }
            })
            .addCase(removeAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Gestion de searchEntriesAsync
            .addCase(searchEntriesAsync.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(searchEntriesAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Les résultats de recherche peuvent remplacer temporairement les données
                // ou être stockés dans un champ séparé selon les besoins
                console.log('🔍 Résultats de recherche reçus:', action.payload);
            })
            .addCase(searchEntriesAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            // ── Cross-slice : entrée créée/modifiée/supprimée via une proposition RAG Écriture ──
            // Une suppression ne renvoie que `resultId` (pas de `resultData`) : sans ce
            // traitement, l'entrée restait affichée dans SecEntree jusqu'au rafraîchissement.
            .addCase(executeProposal.fulfilled, (state, action) => {
                const result = action.payload;
                if (!result.success || result.targetType !== 'entree') return;

                const resultData = result.resultData as any;
                const targetId = resultData?._id || resultData?.id || result.resultId;
                if (!targetId) return;

                const idOf = (e: any) => e._id || e.id;
                const activeId = state.activeEntree ? idOf(state.activeEntree) : undefined;

                if (result.action === 'delete') {
                    state.data = state.data.filter((e: any) => idOf(e) !== targetId);
                    if (activeId === targetId) {
                        state.activeEntree = null;
                    }
                    return;
                }

                if (!resultData) return;

                const idx = state.data.findIndex((e: any) => idOf(e) === targetId);
                if (idx > -1) {
                    state.data[idx] = resultData;
                } else if (state.status !== 'idle') {
                    // Liste jamais chargée : ne rien injecter, sinon la garde
                    // `data.length === 0 && status === 'idle'` de `useEntreesData`
                    // empêcherait le chargement complet de la liste.
                    state.data.push(resultData);
                }

                if (activeId === targetId) {
                    state.activeEntree = resultData;
                }
            });
    }
});

// Référence stable quand le slice est absent (tests, stores partiels) : un `[]`
// neuf à chaque appel ferait rerendre inutilement (avertissement react-redux).
const NO_ENTREES: never[] = [];
export const getEntrees = (state: any) => state.entrees?.data || NO_ENTREES;
export const getEntreesStatus = (state: any) => state.entrees?.status;
export const selectActiveEntree = (state: any) => state.entrees?.activeEntree;
const PORTEE_TOUS: PorteeRapprochement = { sorte: 'tous' };
export const selectPortee = (state: any): PorteeRapprochement => state.entrees?.portee ?? PORTEE_TOUS;
export const selectActiveRefsEntree = (state: any) => state.entrees?.activeRefsEntree;

export default entreeSlice.reducer;
export const { activateEntree, setActiveRefsEntree, setEntrees, setLoading, setError, setSucceeded, setPortee } = entreeSlice.actions;
